/**
 * Route Constants and Builders
 * Type-safe route path generation and navigation
 */

import { RouteBuilder } from './types'

/**
 * Helper to create route builder with parameter interpolation
 */
function createRoute(path: string, children?: Record<string, RouteBuilder>): RouteBuilder {
  return {
    path,
    build: (params?: Record<string, string>) => {
      let finalPath = path
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          finalPath = finalPath.replace(`:${key}`, value)
        })
      }
      return finalPath
    },
    children,
  }
}

/**
 * Centralized route definitions
 * Organized by user role for clarity
 */
export const ROUTES = {
  // Public routes
  public: {
    login: createRoute('/'),
    forgotPassword: createRoute('/forgot-password'),
    resetPassword: createRoute('/reset-password/:uidb64/:token'),
  },

  // PM/Leader routes
  pm: {
    root: createRoute('/'),
    dashboard: createRoute('/programs'),
    programs: createRoute('/programs', {
      list: createRoute('/programs'),
      detail: createRoute('/programs/:programId', {
        overview: createRoute('/programs/:programId'),
        solutions: createRoute('/programs/:programId/solutions'),
        costing: createRoute('/programs/:programId/costing'),
        costRegister: createRoute('/programs/:programId/cost-register'),
        technical: createRoute('/programs/:programId/technical'),
        manufacturing: createRoute('/programs/:programId/manufacturing'),
        vc: createRoute('/programs/:programId/vc'),
        dod: createRoute('/programs/:programId/dod'),
        submissions: createRoute('/programs/:programId/submissions', {
          list: createRoute('/programs/:programId/submissions'),
          detail: createRoute('/programs/:programId/submissions/:submissionId'),
        }),
        documentation: createRoute('/programs/:programId/documentation'),
        settings: createRoute('/programs/:programId/settings'),
      }),
    }),
    market: createRoute('/market'),
    transit: createRoute('/transit', {
      detail: createRoute('/transit/:transitionId'),
    }),
    vendors: createRoute('/vendors', {
      list: createRoute('/vendors'),
      detail: createRoute('/vendors/:vendorId'),
      new: createRoute('/vendors/new'),
    }),
    reviews: createRoute('/reviews', {
      list: createRoute('/reviews'),
      pending: createRoute('/reviews/pending'),
      completed: createRoute('/reviews/completed'),
      detail: createRoute('/reviews/:reviewId'),
    }),
    settings: createRoute('/settings', {
      office: createRoute('/settings/office'),
      user: createRoute('/settings/user'),
    }),
    exec: createRoute('/exec'),
    dib: createRoute('/exec/dib', {
      explore: createRoute('/exec/dib/explore'),
      dashboard: createRoute('/exec/dib/dashboard'),
    }),
  },

  // Reviewer routes
  reviewer: {
    root: createRoute('/reviewer'),
    dashboard: createRoute('/reviewer/dashboard'),
    myReviews: createRoute('/reviewer/my-reviews', {
      list: createRoute('/reviewer/my-reviews'),
      detail: createRoute('/reviewer/my-reviews/:reviewId'),
    }),
    submissions: createRoute('/reviewer/submissions', {
      list: createRoute('/reviewer/submissions'),
      detail: createRoute('/reviewer/submissions/:submissionId'),
    }),
    calendar: createRoute('/reviewer/calendar'),
    settings: createRoute('/reviewer/settings'),
  },

  // Vendor routes
  vendor: {
    root: createRoute('/vendor'),
    dashboard: createRoute('/vendor/dashboard'),
    submissions: createRoute('/vendor/submissions', {
      list: createRoute('/vendor/submissions'),
      new: createRoute('/vendor/submissions/new'),
      detail: createRoute('/vendor/submissions/:submissionId'),
    }),
    opportunities: createRoute('/vendor/opportunities', {
      list: createRoute('/vendor/opportunities'),
      detail: createRoute('/vendor/opportunities/:programId'),
    }),
    company: createRoute('/vendor/company', {
      profile: createRoute('/vendor/company/profile'),
      team: createRoute('/vendor/company/team'),
      documents: createRoute('/vendor/company/documents'),
    }),
    settings: createRoute('/vendor/settings'),
  },

  // Legal routes
  legal: {
    root: createRoute('/legal'),
    dashboard: createRoute('/legal/dashboard'),
    programs: createRoute('/legal/programs', {
      list: createRoute('/legal/programs'),
      detail: createRoute('/legal/programs/:programId'),
    }),
    approvals: createRoute('/legal/approvals', {
      pending: createRoute('/legal/approvals/pending'),
      history: createRoute('/legal/approvals/history'),
    }),
  },

  // Contracting routes
  contracting: {
    root: createRoute('/contracting'),
    dashboard: createRoute('/contracting/dashboard'),
    contracts: createRoute('/contracting/contracts', {
      list: createRoute('/contracting/contracts'),
      detail: createRoute('/contracting/contracts/:contractId'),
      new: createRoute('/contracting/contracts/new'),
    }),
    programs: createRoute('/contracting/programs', {
      list: createRoute('/contracting/programs'),
      detail: createRoute('/contracting/programs/:programId'),
    }),
  },

  // Development/Testing
  dev: {
    showcase: createRoute('/showcase'),
  },
} as const

/**
 * Utility functions for route navigation
 */
export const RouteUtils = {
  /**
   * Build a route path with parameters
   */
  build: (builder: RouteBuilder, params?: Record<string, string>) => {
    return builder.build(params)
  },

  /**
   * Navigate to a route (use with React Router's useNavigate)
   */
  navigate: (
    navigateFn: (path: string) => void,
    builder: RouteBuilder,
    params?: Record<string, string>
  ) => {
    navigateFn(builder.build(params))
  },

  /**
   * Check if current path matches a route
   */
  isActive: (currentPath: string, builder: RouteBuilder, exact: boolean = false) => {
    if (exact) {
      return currentPath === builder.path
    }
    return currentPath.startsWith(builder.path)
  },
}

/**
 * Type-safe navigation hook wrapper
 * Usage:
 * const navigate = useTypedNavigate()
 * navigate(ROUTES.pm.programs.detail, { programId: '123' })
 */
export type TypedNavigate = (
  builder: RouteBuilder,
  params?: Record<string, string>
) => void

